import React, { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGlobe } from "@fortawesome/free-solid-svg-icons";
import { getCountryCallingCode } from "react-phone-number-input/max";
import { arCountryNames, enCountryNames } from "./phoneNumber";
import {
  COUNTRY_GROUP_KEY,
  COUNTRY_LIST_HEIGHT,
  PREFERRED_PHONE_COUNTRIES,
} from "./constants";

/*
 * The country half of the phone control, handed to `react-phone-number-input` as
 * its `countrySelectComponent`. It is an AntD Select so it searches, scrolls a
 * virtual list and drives from the keyboard like every other picker in the app;
 * the library's own control is a native <select> that cannot be searched.
 */

// Arabic spellings vary in a few letters people do not type consistently —
// "السعوديه" should still find "السعودية".
const normalizeSearch = (text) =>
  String(text ?? "")
    .toLowerCase()
    .replace(/[\u064B-\u0652]/g, "")
    .replace(/[أإآ]/g, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .trim();

// "+966", "00966" and "966" all mean the same dialling code, and a code is
// matched from its start — "44" is the UK, not also Angola's "244".
const matchesCountry = (input, option) => {
  const query = normalizeSearch(input).replace(/^(\+|00)/, "");
  if (/^\d+$/.test(query)) return option.code.startsWith(query);
  return option.search.includes(query);
};

// The 250-odd flags are SVG components, ~45 KB gzipped. The field sits in the
// shared barrel and so in the main bundle; loading the flags on the first phone
// field rendered keeps them out of it. Until they arrive each flag is a blank
// tile of the same size, so nothing moves when they do.
let loadedFlags = null;
let flagsRequest = null;
const loadFlags = () =>
  (flagsRequest ??= import("react-phone-number-input/flags").then(
    (module) => (loadedFlags = module.default)
  ));

function useFlags() {
  const [flags, setFlags] = useState(loadedFlags);
  useEffect(() => {
    if (!flags) loadFlags().then(setFlags);
  }, [flags]);
  return flags;
}

export function CountryFlag({ country, flags }) {
  if (!flags) {
    return <span className="phone-field-flag phone-field-flag-pending" />;
  }
  const Flag = country && flags[country];
  if (!Flag) {
    return (
      <FontAwesomeIcon
        icon={faGlobe}
        className="phone-field-flag-fallback"
        aria-hidden
      />
    );
  }
  return <Flag className="phone-field-flag" aria-hidden focusable="false" />;
}

function CountryOption({ name, code, country, flags }) {
  return (
    <span className="phone-field-option">
      <CountryFlag country={country} flags={flags} />
      <span className="phone-field-option-name">{name}</span>
      <span className="phone-field-dial">+{code}</span>
    </span>
  );
}

export default function CountrySelect({
  value,
  onChange,
  onFocus,
  onBlur,
  options,
  disabled,
  readOnly,
  tabIndex,
  "aria-label": ariaLabel,
  onShownCountry,
}) {
  const { t } = useTranslation();
  const flags = useFlags();

  // Reports the country on screen to the number box, which needs the dialling
  // code the flag stands for. It reads the value the picker is showing rather
  // than the library's `onCountryChange`, so the two can never disagree.
  useEffect(() => {
    onShownCountry?.(value);
  }, [value, onShownCountry]);

  const groups = useMemo(() => {
    // The library hands over the countries it supports, already sorted by name
    // in the page's language; an option without a value is its separator.
    const countries = (options ?? [])
      .filter((option) => option.value)
      .map(({ value: country, label }) => ({
        value: country,
        label,
        code: getCountryCallingCode(country),
        search: normalizeSearch(
          `${enCountryNames[country]} ${arCountryNames[country]} ${country}`
        ),
      }));

    const preferred = PREFERRED_PHONE_COUNTRIES.map((country) =>
      countries.find((option) => option.value === country)
    ).filter(Boolean);
    const rest = countries.filter(
      (option) => !PREFERRED_PHONE_COUNTRIES.includes(option.value)
    );

    return [
      {
        key: COUNTRY_GROUP_KEY.SUGGESTED,
        label: t("phoneInput.suggested"),
        options: preferred,
      },
      {
        key: COUNTRY_GROUP_KEY.ALL,
        label: t("phoneInput.allCountries"),
        options: rest,
      },
    ];
  }, [options, t]);

  return (
    <Select
      className="phone-field-country"
      classNames={{ popup: { root: "phone-field-country-popup" } }}
      showSearch
      value={value}
      options={groups}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={disabled}
      open={readOnly ? false : undefined}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      // Groups reach the filter too; only countries carry a `code`.
      filterOption={(input, option) =>
        Boolean(option.code) && matchesCountry(input, option)
      }
      // The flag alone: the dialling code is written in the number box.
      labelRender={({ value: country }) => (
        <span className="phone-field-selected">
          <CountryFlag country={country} flags={flags} />
        </span>
      )}
      optionRender={({ data }) => (
        <CountryOption
          name={data.label}
          code={data.code}
          country={data.value}
          flags={flags}
        />
      )}
      popupMatchSelectWidth={false}
      listHeight={COUNTRY_LIST_HEIGHT}
      notFoundContent={
        <span className="phone-field-empty">{t("phoneInput.noCountry")}</span>
      }
    />
  );
}
